import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { SuspenseBoundary } from '@/ui/components/suspense-boundary';
import { BidsBreadcrumbs } from '@/features/bids/components/bids-breadcrumbs';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Header, HeaderChatButton, HeaderBackButton } from '@/ui/components/header';
import { H1, H3 } from '@/ui/components/heading';
import { MainContainer, MainHeader, MainScrollArea } from '@/ui/components/main';

import { PlusSignIcon } from '@hugeicons-pro/core-stroke-rounded';
import { Icon } from '@/ui/components/icon';
import { BidsList } from '@/features/bids/components/bids-list';
import { BidsAnalysisCards } from '@/features/bids/components/bids-analysis-cards';
import { Button } from '@/ui/components/button';
import { createBidOpenAtom, lastOpenedBidByTenderAtom } from '@/features/bids/store';
import { CreateBid } from '@/features/bids/components/create-bid';
import { CreateBidComparisonSheet } from '@/features/bid-comparison/components/create-bid-comparison-sheet';
import { useSuspenseQuery } from '@tanstack/react-query';
import { bidsQuery, tenderBidDocumentsQuery } from '@/features/bids/queries';
import { IndexingProgressAlert } from '@/features/documents/components/indexing-progress-alert';
import { projectDetailsQuery } from '@/features/projects/queries';
import { useProjectView } from '@/features/projects/helpers';
import { getDefaultStore, useSetAtom } from 'jotai';
import { Tooltip, TooltipPopup, TooltipTrigger } from '@/ui/components/tooltip';
import { useDocumentsIndexingStatus } from '@/features/documents/queries';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/bids/_layout/',
)({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    const project = await context.queryClient.query({
      ...projectDetailsQuery(params.projectId),
      staleTime: 'static',
    });
    if (!project.companyId) return;

    const bids = await context.queryClient.query({
      ...bidsQuery(params.workspaceId),
      staleTime: 'static',
    });
    const lastOpenedBidId = getDefaultStore().get(lastOpenedBidByTenderAtom)[params.workspaceId];
    const bidId = bids.data.find((bid) => bid.id === lastOpenedBidId)?.id ?? bids.data[0]?.id;
    if (!bidId)
      throw redirect({
        to: '/project/$projectId/workspaces/$workspaceId',
        params,
        replace: true,
      });
    throw redirect({
      to: '/project/$projectId/workspaces/$workspaceId/bids/$bidId',
      params: { ...params, bidId },
      replace: true,
    });
  },
});

function RouteComponent() {
  const { projectId, workspaceId } = Route.useParams();
  const view = useProjectView();
  const setCreateBidOpen = useSetAtom(createBidOpenAtom);

  const { areDocumentsIndexed: isTenderPackageFullyIndexed, hasNoDocuments: tenderHasNoDocuments } =
    useDocumentsIndexingStatus({
      documentType: 'tenderPackage',
      id: workspaceId,
    });

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/workspaces/$workspaceId"
          params={{ projectId, workspaceId }}
        />
        <BidsBreadcrumbs />
        <HeaderChatButton />
      </Header>
      <MainScrollArea>
        <SuspenseBoundary>
          <BidDocumentsIndexingAlert />
        </SuspenseBoundary>
        <MainHeader>
          <H1>{t({ en: 'Submissions', nb: 'Innleveringer' })}</H1>
          <Tooltip>
            <TooltipTrigger
              delay={0}
              render={
                <Button
                  className="max-md:w-full"
                  kind="with-icon"
                  onClick={() => setCreateBidOpen(true)}
                  disabled={tenderHasNoDocuments || !isTenderPackageFullyIndexed}
                >
                  <Icon icon={PlusSignIcon} />
                  {t({ en: 'New supplier bid', nb: 'Nytt leverandørtilbud' })}
                </Button>
              }
            />
            {tenderHasNoDocuments ? (
              <TooltipPopup>{t(common.noTenderPackageDocuments)}</TooltipPopup>
            ) : !isTenderPackageFullyIndexed ? (
              <TooltipPopup>
                {t({
                  en:
                    view === 'client'
                      ? 'Please wait until tender package files are analysed before creating a submission.'
                      : 'Please wait until tender package files are analysed before creating a bid.',
                  nb:
                    view === 'client'
                      ? 'Vent til anbudspakkefilene er analysert før du oppretter en innlevering.'
                      : 'Vent til anbudspakkefilene er analysert før du oppretter et tilbud.',
                })}
              </TooltipPopup>
            ) : null}
          </Tooltip>
        </MainHeader>
        <MainContainer className="pb-6 pt-1">
          <BidsAnalysisCards />
          <H3 className="mb-3">{t({ en: 'Supplier bids', nb: 'Leverandørtilbud' })}</H3>
          <BidsList />
        </MainContainer>
        <CreateBid />
        <CreateBidComparisonSheet />
      </MainScrollArea>
    </>
  );
}

function BidDocumentsIndexingAlert() {
  const { workspaceId } = Route.useParams();
  const { data: bidDocuments } = useSuspenseQuery(tenderBidDocumentsQuery(workspaceId));
  return (
    <IndexingProgressAlert
      progress={bidDocuments.indexing.progress}
      isCompleted={bidDocuments.indexing.isIndexed}
      isStalled={bidDocuments.indexing.isStalled}
    />
  );
}
